import { Fragment, type ReactNode } from 'react';
import { renderProvenanceItem } from './ProvenanceItemRenderer';
import type { AgentHistoryItem } from './provenance-entry-item';

export interface ProvenanceTranscriptProps {
  items: AgentHistoryItem[];
  onOpenContent?: (contentId: number) => void;
  density?: 'comfortable' | 'compact';
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  emptyState?: ReactNode;
  sectionTitle?: string;
}

export function ProvenanceTranscript({
  items,
  onOpenContent,
  density = 'comfortable',
  isLoading = false,
  isError = false,
  errorMessage = 'Failed to load history.',
  emptyState = null,
  sectionTitle,
}: ProvenanceTranscriptProps) {
  const outerSpacing = density === 'comfortable' ? 'space-y-4' : 'space-y-3';

  const body = ((): ReactNode => {
    if (isLoading) {
      return <p className="text-sm text-muted-foreground">Loading history…</p>;
    }
    if (isError) {
      return <p className="text-sm text-destructive">{errorMessage}</p>;
    }
    if (items.length === 0) {
      return emptyState;
    }
    return (
      <div className={outerSpacing}>
        {items.map((item) => (
          <Fragment key={`${item.kind}:${item.id}`}>
            {renderProvenanceItem(item, { onOpenContent, density })}
          </Fragment>
        ))}
      </div>
    );
  })();

  if (sectionTitle) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-muted-foreground">{sectionTitle}</p>
        {body}
      </div>
    );
  }

  return <>{body}</>;
}
